/**
 * @module employees ui
 */
import { createReducer } from '@reduxjs/toolkit';
import { uiActions } from '~/behaviors/actions/ui.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { employeeAttributeActions } from '~/behaviors/actions/employee_attribute.actions';
import { employeeActions } from '~/behaviors/actions/employee.actions';
import { convert } from '~/tools/convert';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';

const defaultState = {
  search: '',
  attributeValues: {},
  filterBy: {
    isActive: true,
  },
  orderBy: 'name',
  order: 'asc',
  page: 0,
  per: 15,
  count: 0,
  isLoadingEmployees: false,
  isLoadingEmployeeAttributes: false,
  showEmployeeDialog: false,
  pageEmployeeIds: [],
  isUpdatingEmployeeIds: false,
};

const sanitizeFilters = (filterBy) => {
  let isActive = filterBy?.isActive;

  // Force isActive to Boolean if not `null` (is a string 'true' or 'false').
  if (isActive !== undefined && isActive !== null) {
    isActive = isActive === true || isActive === 'true';
  }

  const updatedFilterBy = {
    ...filterBy,
    isActive,
  };

  const sanitizedFilterBy = convert.pick(updatedFilterBy, Object.keys(defaultState.filterBy));

  return sanitizedFilterBy;
};

const reset = () => defaultState;

const merge = (updates) => (state) => ({
  ...state,
  ...updates,
});

const init = (state, action) => {
  const employeeCount = action?.payload?.count || 0;

  state.count = employeeCount;
  state.isLoadingEmployees = false;
};

const paginate = (state, action) => {
  const { search, attributeValues, filterBy, orderBy, order, page, per, count } = handleUnsafeOptionalChaining(
    action?.payload?.pagination,
  );
  const pageEmployeeIds = action?.payload?.pageEmployeeIds;
  const sanitizedFilterBy = sanitizeFilters(filterBy);

  return {
    ...state,
    search,
    attributeValues, // overwrite any existing attributeValues with new ones
    filterBy: {
      ...state.filterBy,
      ...sanitizedFilterBy,
    },
    orderBy,
    order,
    page,
    per,
    count,
    pageEmployeeIds,
    isLoadingEmployees: false,
    hasPendingExport: false,
    hasPendingImport: false,
  };
};

const changeUI = (state, action) => {
  const updates = action?.payload || {};
  const sanitizedFilterBy = sanitizeFilters({
    ...state.filterBy,
    ...updates.filterBy,
  });

  // Disallow directly changing pagination-related attributes (use `paginate()` instead).
  const sanitizedAttrs = convert.pick(updates, [
    'isLoadingEmployees',
    'isLoadingEmployeeAttributes',
    'showEmployeeDialog',
    'attributeValues',
  ]);

  return {
    ...state,
    ...sanitizedAttrs,
    filterBy: sanitizedFilterBy,
  };
};

const employeesUI = createReducer(defaultState, (builder) =>
  builder
    .addCase(metaActions.reset, reset)
    .addCase(uiActions.changeEmployeesUI, changeUI)
    .addCase(employeeActions.fetchEmployees.pending, merge({ isLoadingEmployees: true }))
    .addCase(employeeActions.fetchEmployees.fulfilled, init)
    .addCase(employeeActions.fetchEmployees.rejected, merge({ isLoadingEmployees: false }))
    .addCase(employeeActions.pageEmployees.pending, merge({ isLoadingEmployees: true }))
    .addCase(employeeActions.pageEmployees.fulfilled, paginate)
    .addCase(employeeActions.pageEmployees.rejected, merge({ isLoadingEmployees: false }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.pending, merge({ isLoadingEmployeeAttributes: true }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.fulfilled, merge({ isLoadingEmployeeAttributes: false }))
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.rejected, merge({ isLoadingEmployeeAttributes: false }))
    .addCase(employeeActions.importEmployees.pending, merge({ hasPendingImport: true }))
    .addCase(employeeActions.importEmployees.fulfilled, merge({ hasPendingImport: false }))
    .addCase(employeeActions.importEmployees.rejected, merge({ hasPendingImport: false }))
    .addCase(employeeActions.exportEmployees.pending, merge({ hasPendingExport: true }))
    .addCase(employeeActions.exportEmployees.fulfilled, merge({ hasPendingExport: false }))
    .addCase(employeeActions.exportEmployees.rejected, merge({ hasPendingExport: false }))
    .addCase(employeeActions.updateEmployeeIds.pending, merge({ isUpdatingEmployeeIds: true }))
    .addCase(employeeActions.updateEmployeeIds.fulfilled, merge({ isUpdatingEmployeeIds: false }))
    .addCase(employeeActions.updateEmployeeIds.rejected, merge({ isUpdatingEmployeeIds: false })),
);

export default employeesUI;
